import { AuditLogEvent } from './../types';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { all, fork } from 'redux-saga/effects';
import {
  Appearance,
  ContentType,
  Folder,
  IAdvancedFilters,
  IPageInfo,
  Message,
  ModerationSearchType,
  Post,
  SpotPolicy,
  TagsSpanScores,
  TextAnalyzerTag,
  User,
  ViewType
} from '../types';
import postsRealtimeCounter from './realtime-count';
import commentsRealtimeCounter from './comments-realtime-count';
import { loadArticlesSagas, watchToggleReadOnlySaga } from '../../../articles/reducers/loadArticlesSlice';
import { restrictedWordsSagas } from '../../../restricted-words-next/restrictedWordsSagas';
import unlockComments from '../../api/unlock-comments';
import { commentsSagas } from '../sagas/commentsSagas';

export const getSpotItemsStart = createAction<{ start: boolean; viewType: ViewType }>(
  'GET_SPOT_ITEMS_START'
);
export const getSpotItemsSuccess = createAction<{
  users: User[];
  posts?: Post[];
  messages: Message[];
  folder: Folder;
  expirationTime: Date | string;
  folderItemsCount: number;
  spotPolicy: SpotPolicy;
}>('GET_SPOT_ITEMS_SUCCESS');

export const getSpotItemsFailure = createAction<{ error: Error }>('GET_SPOT_ITEMS_FAILURE');

export const setPageInfo = createAction<Partial<IPageInfo>>('SET_PAGE_INFO');
export const setPageLoading = createAction<boolean>('SET_PAGE_LOADING');

export const setFolderCount = createAction<{ folderItemsCount: number; folder?: Folder }>(
  'SET_FOLDER_COUNT'
);

export const toggleItem = createAction<{ id: string; checked: boolean; cursor: number }>(
  'SELECT_ITEM'
);

export const selectAllItems = createAction('SELECT_ALL_ITEMS');

export const clearAllItems = createAction('CLEAR_ALL_ITEMS');

export const resetAllFilters = createAction<{
  pageInfo?: Partial<IPageInfo>;
  filters?: Partial<IAdvancedFilters>;
  search?: { searchType: ModerationSearchType; searchValue: string };
  isLoading?: boolean;
}>('RESET_ALL_FILTERS');

export const resetModerationFilters = createAction('RESET_MODERATION_FILTERS');

export const approveItemsStart = createAction<{
  messageIds: string[];
  appearance: Appearance;
  folder: Folder;
}>('APPROVE_ITEMS_START');

export const approveItemsSuccess = createAction<{
  messageIds: string[];
  appearance: Appearance;
  folder: Folder;
}>('APPROVE_ITEMS_SUCCESS');

export const approveItemsFailure = createAction<{ error?: Error; messageIds: string[] }>(
  'APPROVE_ITEMS_FAILURE'
);

export const rejectItemsStart = createAction<{
  messageIds: string[];
  appearance: Appearance;
  folder: Folder;
}>('REJECT_ITEMS_START');

export const rejectItemsSuccess = createAction<{
  messageIds: string[];
  appearance: Appearance;
  folder: Folder;
}>('REJECT_ITEMS_SUCCESS');

export const rejectItemsFailure = createAction<{ error?: Error; messageIds: string[] }>(
  'REJECT_ITEMS_FAILURE'
);

export const startPostsRealtimeCounter = createAction('START_POSTS_REALTIME_COUNTER');

export const cancelPostsRealtimeCounter = createAction('CANCEL_POSTS_REALTIME_COUNTER');

export const fetchCountSuccess = createAction<{
  counts: { [key in Folder]?: number };
  polling: boolean;
  contentType: ContentType;
}>('FETCH_COUNT_SUCCESS');

export const fetchCountFailure = createAction<{ error: Error }>('FETCH_COUNT_FAILURE');

export const startCommentsRealtimeCounter = createAction('START_COMMENTS_REALTIME_COUNTER');

export const cancelCommentsRealtimeCounter = createAction('CANCEL_COMMENTS_REALTIME_COUNTER');

export const setClientAppearance = createAction<{
  id: string;
  appearance: Appearance;
  folder: Folder;
}>('SET_CLIENT_APPEARANCE');

export const searchModerationItemsSuccess = createAction<{
  searchType: ModerationSearchType;
  searchValue: string;
  userId?: string;
}>('SEARCH_MODERATION_ITEMS_SUCCESS');

export const searchModerationItemsFailure = createAction<{
  searchType: ModerationSearchType;
  searchValue: string;
  userId?: string;
  error: Error | string;
}>('SEARCH_MODERATION_ITEMS_FAILURE');

export const changePageFilters = createAction<{
  filters: Partial<IAdvancedFilters>;
  search: { searchValue: string; searchType: ModerationSearchType };
  preventOffsetReset?: boolean;
}>('CHANGE_PAGE_FILTERS');

export const findUserSuggestionsSuccess = createAction<{ suggestions: any[] }>(
  'MODERATION_FIND_USER_SUCCESS'
);

export const findUserSuggestionsFailure = createAction('MODERATION_FIND_USER_FAILURE');

export const clearUserSuggestions = createAction('MODERATION_CLEAR_USER_SUGGESTIONS');

export const toggleModerationActiveBar = createAction('MODERATION_ACTIVE_BAR/TOGGLE');

export const openModerationActiveBar = createAction('MODERATION_ACTIVE_BAR/OPEN');

export const closeModerationActiveBar = createAction('MODERATION_ACTIVE_BAR/CLOSE');
export const setConversationPolicy = createAction<{ conversationId: string; policy: SpotPolicy }>(
  'SET_CONVERSATION_POLICY'
);

export const toggleModerationMode = createAsyncThunk(
  'TOGGLE_MODERATION_MODE_THUNK',
  function(
    payload: {
      value?: boolean;
      skipReleaseLock?: boolean;
    },
    { dispatch, getState }
  ) {
    const { activeItems } = (getState() as any).moderation.messages;
    const { value, skipReleaseLock } = payload;
    if (!value && activeItems > 0 && !skipReleaseLock) {
      unlockComments();
    }

    dispatch(toggleModerationModeAction(payload));
  }
);

export const toggleModerationModeAction = createAction<{ value?: boolean }>(
  'TOGGLE_MODERATION_MODE'
);

export const fetchMessageAuditLogSuccess = createAction<{
  messageId: string;
  users: Record<string, any>;
  timeline: AuditLogEvent | AuditLogEvent[];
  isOnlyManual: boolean;
}>('FETCH_MESSAGE_AUDIT/SUCCESS');

export const fetchMessageAuditLogFailure = createAction<{
  messageId: string;
  isOnlyManual: boolean;
}>('FETCH_MESSAGE_AUDIT/FAILURE');

export const fetchMessageTagsSpansSuccess = createAction<{
  messageId: string;
  tagName: TextAnalyzerTag;
  spanScoreResults: TagsSpanScores;
  isEmpty: boolean;
}>('FETCH_MESSAGE_TAGS_SPANS_SUCCESS');

export const fetchHighlightTagsConfig = createAction<{ isHighlightTagsEnabled: boolean }>(
  'FETCH_HIGHLIGHT_TAGS_CONFIG'
);

export function* moderationSaga() {
  yield all(
    [
      postsRealtimeCounter,
      commentsRealtimeCounter,
      loadArticlesSagas,
      watchToggleReadOnlySaga,
      restrictedWordsSagas,
      commentsSagas
    ].map(saga => fork(saga))
  );
}
